import { AppShell, NavLink, type NavLinkStylesNames, Text } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBook,
  IconCreditCard,
  IconForms,
  IconHelp,
  IconHome2,
  IconKey,
  IconLogout,
  IconMessage,
  IconPlugConnected,
  IconRouteAltLeft,
  IconSettings,
  IconShieldCheck,
  IconUsers,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { useRustyState } from '../../state';

const ACTIVE_NAVLINK_STYLES: Partial<Record<NavLinkStylesNames, React.CSSProperties>> = {
  root: {
    backgroundColor: 'var(--mantine-color-body)',
    border: '1px solid var(--mantine-color-default-border)',
    borderRadius: 'var(--mantine-radius-md)',
    boxShadow: 'var(--mantine-shadow-xs)',
    position: 'relative',
    zIndex: 1,
  },
  label: {
    color: 'var(--mantine-primary-color-filled)',
    fontWeight: 600,
  },
  section: {
    color: 'var(--mantine-primary-color-filled)',
  },
};

const stylesIfActive = (active: boolean) => (active ? ACTIVE_NAVLINK_STYLES : undefined);

export function AccountNavigation() {
  const { t } = useTranslation();
  const { createLanguageURL, isActive } = useLanguageAwareRouting();

  const logout = async () => {
    if (!(await useRustyState.getState().logout())) {
      window.location.replace(createLanguageURL('/'));
    }
  };

  const sectionLabelProps = {
    tt: 'uppercase' as const,
    size: 'xs' as const,
    fw: 600,
    c: 'dimmed',
    pl: 'xs',
    mb: 4,
  };

  const billingActive =
    isActive('/account/billing/usage', true) || isActive('/account/billing/subscriptions', true);

  return (
    <AppShell.Navbar p="md" style={{ display: 'flex', flexDirection: 'column' }}>
      <Text {...sectionLabelProps}>{t('glob_navigation.sectionWorkspace')}</Text>
      <NavLink
        component={Link}
        to={createLanguageURL('/account')}
        label={t('glob_navigation.home')}
        leftSection={<IconHome2 size="18" stroke={1.5} />}
        active={isActive('/account')}
        styles={stylesIfActive(isActive('/account'))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/forms')}
        label={t('glob_navigation.forms')}
        leftSection={<IconForms size="18" stroke={1.5} />}
        active={isActive('/account/forms', true)}
        styles={stylesIfActive(isActive('/account/forms', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/messages')}
        label={t('glob_navigation.messages')}
        leftSection={<IconMessage size="18" stroke={1.5} />}
        active={isActive('/account/messages', true)}
        styles={stylesIfActive(isActive('/account/messages', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/issues')}
        label={t('issues.title')}
        leftSection={<IconAlertTriangle size="18" stroke={1.5} />}
        active={isActive('/account/issues', true)}
        styles={stylesIfActive(isActive('/account/issues', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/integrations')}
        label={t('glob_navigation.integrations')}
        leftSection={<IconRouteAltLeft size="18" stroke={1.5} />}
        active={isActive('/account/integrations', true)}
        styles={stylesIfActive(isActive('/account/integrations', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/mcp')}
        label={t('glob_navigation.mcp')}
        leftSection={<IconPlugConnected size="18" stroke={1.5} />}
        active={isActive('/account/mcp', true)}
        styles={stylesIfActive(isActive('/account/mcp', true))}
      />

      <Text {...sectionLabelProps} mt="md">
        {t('glob_navigation.sectionAccount')}
      </Text>
      <NavLink
        label={t('glob_navigation.billing')}
        leftSection={<IconCreditCard size="18" stroke={1.5} />}
        active={billingActive}
        styles={stylesIfActive(billingActive)}
      >
        <NavLink
          component={Link}
          to={createLanguageURL('/account/billing/usage')}
          label={t('glob_navigation.usageAndCredits')}
          active={isActive('/account/billing/usage', true)}
          styles={stylesIfActive(isActive('/account/billing/usage', true))}
        />
        <NavLink
          component={Link}
          to={createLanguageURL('/account/billing/subscriptions')}
          label={t('glob_navigation.subscriptions')}
          active={isActive('/account/billing/subscriptions', true)}
          styles={stylesIfActive(isActive('/account/billing/subscriptions', true))}
        />
      </NavLink>
      <NavLink
        component={Link}
        to={createLanguageURL('/account/referrals')}
        label={t('glob_navigation.referrals')}
        leftSection={<IconUsers size="18" stroke={1.5} />}
        active={isActive('/account/referrals', true)}
        styles={stylesIfActive(isActive('/account/referrals', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/api-keys')}
        label={t('glob_navigation.apiKeys')}
        leftSection={<IconKey size="18" stroke={1.5} />}
        active={isActive('/account/api-keys', true)}
        styles={stylesIfActive(isActive('/account/api-keys', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/profile')}
        label={t('glob_navigation.profile')}
        leftSection={<IconSettings size="18" stroke={1.5} />}
        active={
          isActive('/account/profile', true) ||
          isActive('/account/connected', true) ||
          isActive('/account/linked', true)
        }
        styles={stylesIfActive(
          isActive('/account/profile', true) ||
            isActive('/account/connected', true) ||
            isActive('/account/linked', true)
        )}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/security-log')}
        label={t('glob_navigation.securityLog')}
        leftSection={<IconShieldCheck size="18" stroke={1.5} />}
        active={isActive('/account/security-log', true)}
        styles={stylesIfActive(isActive('/account/security-log', true))}
      />

      <Text {...sectionLabelProps} mt="md">
        {t('glob_navigation.sectionHelp')}
      </Text>
      <NavLink
        component={Link}
        to={createLanguageURL('/account/docs')}
        label={t('glob_navigation.docs')}
        leftSection={<IconBook size="18" stroke={1.5} />}
        active={isActive('/account/docs', true)}
        styles={stylesIfActive(isActive('/account/docs', true))}
      />
      <NavLink
        component={Link}
        to={createLanguageURL('/account/support')}
        label={t('glob_navigation.support')}
        leftSection={<IconHelp size="18" stroke={1.5} />}
        active={isActive('/account/support', true)}
        styles={stylesIfActive(isActive('/account/support', true))}
      />

      <NavLink
        onClick={logout}
        href="#"
        label={t('glob_navigation.logout')}
        leftSection={<IconLogout size="18" stroke={1.5} />}
        mt="auto"
      />
    </AppShell.Navbar>
  );
}
